'use client'

import Script from 'next/script'
import { useEffect, useMemo, useState } from 'react'

import { SocialLadderSkeleton } from '@src/components/SocialLadder/SocialLadderSkeleton'
import { configPublic } from '@src/config.public'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

interface Props {
  className?: string
  flow: 'ambassadorDashboard' | 'affiliateApplication'
  deviceUUID?: string | null
  onLoad?: () => void
  onError?: () => void
}

export function SocialLadderIframe({ className, flow, deviceUUID, onLoad, onError }: Props) {
  const { locale } = useLocale()
  const { user } = useSession()
  const { edit } = getBuilderPreviewMode()
  const [isLoading, setIsLoading] = useState(true)

  // Iframe IDs and initialisation functions for each flow
  const flows = useMemo(
    () => ({
      ambassadorDashboard: {
        iframeId: 'slWebFrame',
        skeleton: SocialLadderSkeleton,
        init: () => {
          if (!deviceUUID) return
          const areaGuid = configPublic.socialLadder.areaGuid.BodiExperience
          window.loadSLWebFrame(areaGuid, deviceUUID, '', '', '', locale.lang)
        },
      },
      affiliateApplication: {
        iframeId: 'slWebAppWidget',
        skeleton: null,
        init: () => {
          const areaGuid = configPublic.socialLadder.areaGuid.BodiAffiliates
          const appGuid = configPublic.socialLadder.appGuid.BodiAffiliates
          const campGuid = window.getParameterFromURLByName('campGuid')
          const resGuid = window.getParameterFromURLByName('resGuid')
          const crmShopName = configPublic.socialLadder.shopCode.BodiAffiliates
          const customerEmail = user?.email ?? ''
          const customerId = user?.guid ?? ''
          window.loadSLApplicationWidget(
            areaGuid,
            appGuid,
            customerEmail,
            customerId,
            campGuid,
            crmShopName,
            resGuid,
            locale.lang
          )
        },
      },
    }),
    [deviceUUID, user?.email, user?.guid, locale.lang]
  )

  // When the SocialLadder's iframe has loaded, set the loading state to false
  useEffect(() => {
    const handleMessage = (message: MessageEvent) => {
      if (message.origin == 'https://socialladder.rkiapps.com' && message.data.action == 'loaded') {
        setIsLoading(false)
        onLoad?.()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onLoad])

  if (edit) return <BuilderPreview />

  return (
    <div className="flex w-full justify-center">
      <div
        id={flows[flow].iframeId}
        className={cn(className, { hidden: !!flows[flow].skeleton && isLoading })}
      />

      {!!flows[flow].skeleton && isLoading && flows[flow].skeleton()}

      <Script
        src="https://socialladder.rkiapps.com/SLWeb/slweb-frame.js"
        onReady={flows[flow].init}
        onError={onError}
      />
    </div>
  )
}

function BuilderPreview() {
  return (
    <div className="mx-auto flex w-full max-w-[590px]">
      <div className="flex w-full flex-col gap-4 rounded-lg border border-gray-300 bg-white p-6">
        <div className="h-10 rounded bg-gray-200" />
        <div className="flex gap-4">
          <div className="h-10 flex-1 rounded bg-gray-200" />
          <div className="h-10 flex-1 rounded bg-gray-200" />
        </div>
        <div className="flex gap-4">
          <div className="h-10 w-24 rounded bg-gray-200" />
          <div className="h-10 flex-1 rounded bg-gray-200" />
        </div>
        <div className="flex gap-4">
          <div className="h-10 flex-1 rounded bg-gray-200" />
          <div className="h-10 flex-1 rounded bg-gray-200" />
        </div>
        <div className="h-5 w-2/3 rounded bg-gray-200" />
        <div className="h-5 w-1/2 rounded bg-gray-200" />
        <div className="mt-6 flex items-center justify-between">
          <div className="h-4 w-12 rounded bg-gray-200" />
          <div className="h-10 w-24 rounded bg-gray-300" />
        </div>
      </div>
    </div>
  )
}
